import React, { memo } from 'react';
import { useAtomValue } from 'jotai';
import { selectedProductAtom } from '../atoms';
import { FeatureMsgWrapper, FeatureText } from '@src/components/common/FeatureCheck/styled';
import { CURRENT_PRODUCT_MODE, isAppModule, PRODUCT_MODE_MAP } from '@src/utils/constants';
import AppSensitivityRow from './AppSensitivityRow';
import AdSensitivityRow from './AdSensitivityRow';
import SensitivityRow from './SensitivityRow';
import KpiMetricsSection from './KpiMetricsSection';
import { StyledCollapse } from './styled';
import { Collapse } from 'antd';
import { isSearchedItem } from '../utils';
import PanelHeader from './PanelHeader';
import { NoData } from '@src/components/common/Retry/styled';
import { useGlobalFeature } from '@src/globalStore/globalFeatureFlagsStore';
import { globalFeatureNames } from '@src/utils/featureToggle/constants';
import {
  AI_ALERT_KPI_BETA_METRICS,
  AI_ALERT_STANDARD_METRICS_MAP,
} from '@src/components/ai-alerts/constants';

const SensitivityForm = ({ categoryMap, sensitivityList, savedSensitivityList, searchText }) => {
  const selectedProduct = useAtomValue(selectedProductAtom);
  const collapsible: boolean = Object.keys(categoryMap).length > 1;
  const enableDPICustomDimension = useGlobalFeature(globalFeatureNames.DPI_CUSTOM_DIMENSION);
  const isEi = selectedProduct === PRODUCT_MODE_MAP.EI;

  const isExistingMetric = (metricName) => {
    return (
      AI_ALERT_STANDARD_METRICS_MAP[CURRENT_PRODUCT_MODE].includes(metricName) || // standard metric has to always show ai cohort button
      savedSensitivityList.some((sensitivity) => sensitivity.metricKey === metricName)
    );
  };

  const isExistingFlow = (metrics) => {
    return metrics.some((metric) => isExistingMetric(metric.name));
  };

  const getSensitivityRow = (
    sensitivity,
    showAICohortButtonPerMetric,
    disableAICohortButton,
    metricInfo = { name: undefined, isBeta: false },
  ) => {
    // Existing EI rows stay visible (with a checkbox) even when deselected so they can be
    // re-enabled; non-EI rows still need a saved config to render.
    if (!sensitivity && selectedProduct !== PRODUCT_MODE_MAP.EI) return null;

    switch (selectedProduct) {
      case PRODUCT_MODE_MAP.AI:
        return <AdSensitivityRow key={sensitivity.metricKey} sensitivity={sensitivity} />;
      case PRODUCT_MODE_MAP.APP:
        return (
          <AppSensitivityRow
            key={sensitivity.metricKey}
            sensitivity={sensitivity}
            searchText={searchText}
            showAICohortButton={showAICohortButtonPerMetric}
            disableAICohortButton={disableAICohortButton}
          />
        );
      default:
        // Existing EI metrics are also selectable (checkbox, checked by default); Beta KPI
        // metrics are rendered separately in the KPI section, so they aren't selectable here.
        return (
          <SensitivityRow
            key={sensitivity?.metricKey ?? metricInfo.name}
            sensitivity={sensitivity}
            isBeta={metricInfo.isBeta}
            selectable={isEi && !metricInfo.isBeta}
            metricKey={metricInfo.name}
          />
        );
    }
  };

  // Beta KPI metrics are pulled out of the normal category list and shown together with the
  // KPI dropdown in their own box at the top of the popup (respecting the active search filter).
  const betaMetricEntries = isEi
    ? categoryMap.flatMap(({ metrics }) =>
        metrics.filter(
          ({ name, displayName }) =>
            AI_ALERT_KPI_BETA_METRICS.includes(name) && isSearchedItem(displayName, searchText),
        ),
      )
    : [];

  const allMetricEntries = categoryMap.flatMap(({ metrics }) => metrics);
  const allMetricNames = allMetricEntries.map(({ name }) => name);
  const showSharedCohortsButton = isEi && enableDPICustomDimension && !isAppModule;

  return (
    <div style={{ paddingTop: '15px' }}>
      {categoryMap && categoryMap.length > 0 ? (
        <>
          {isEi ? (
            <KpiMetricsSection
              betaMetricEntries={betaMetricEntries}
              sensitivityList={sensitivityList}
              allMetricNames={allMetricNames}
              showAICohortButton={showSharedCohortsButton}
              disableAICohortButton={!isExistingFlow(allMetricEntries)}
            />
          ) : null}
          {categoryMap.map(({ key, metrics, isFlow }) =>
            metrics.length > 0 ? (
              <StyledCollapse
                defaultActiveKey={[`${key}key`]}
                key={key}
                collapsible={collapsible ? 'header' : 'disabled'}
              >
                <Collapse.Panel
                  key={`${key}key`}
                  header={
                    <PanelHeader
                      metric={key}
                      metrics={metrics.map(({ name }) => name)}
                      showAICohortButton={
                        !isEi && enableDPICustomDimension && (isFlow || !isAppModule)
                      }
                      disableAICohortButton={!isExistingFlow(metrics)}
                      isFlow={isFlow}
                    />
                  }
                  showArrow={collapsible}
                >
                  {metrics.map(({ name, displayName }) => {
                    // Beta KPI metrics live in the dedicated KPI section above.
                    if (AI_ALERT_KPI_BETA_METRICS.includes(name)) {
                      return null;
                    }
                    const sensitivity = sensitivityList.find(
                      (sensitivity) => sensitivity.metricKey === name,
                    );
                    const showAICohortButtonPerMetric =
                      enableDPICustomDimension && isAppModule && !isFlow;

                    return isSearchedItem(displayName, searchText)
                      ? getSensitivityRow(
                          sensitivity,
                          showAICohortButtonPerMetric,
                          !isExistingMetric(name),
                          { name, isBeta: false },
                        )
                      : null;
                  })}
                </Collapse.Panel>
              </StyledCollapse>
            ) : null,
          )}
        </>
      ) : searchText ? (
        <NoData emptyPlaceholder="No metrics found for your search." />
      ) : (
        <FeatureMsgWrapper>
          <FeatureText>
            {/* If AI Alert dashboard is available, then default metrics will be there, this corner case can only exist for AD tab */}
            {
              'Metrics are not available for sensitivity configuration. Contact your Conviva representative for more information.'
            }
          </FeatureText>
        </FeatureMsgWrapper>
      )}
    </div>
  );
};

export default memo(SensitivityForm);
